import { rawTargetOf } from "./kpiTargetScale";

/**
 * Where a derived metric gets its goal.
 *
 * "Technicians' Revenue per Hour" is not a KPI document, so it carries no target
 * of its own. But a company that tracks the same figure as a real metric — an
 * anchor composite, Total Revenue ÷ Hours Worked, with a goal set on it — has
 * already said what "good" is for it. That metric is the derived tile's TWIN: the
 * derived number is company Total Revenue over Technicians' Hours, and the twin is
 * the composite built from those very two source KPIs.
 *
 * Matched on the two source KPI ids, never on a title: a metric that merely
 * happens to be called "Revenue per Hour" (Field Sales' revenue earned per hour,
 * say) divides something else and would hand the tile somebody else's goal.
 *
 * Anchor metrics only. A progressive target is a per-day figure that is spread
 * over the window; an hourly RATE is not earned a day at a time, so a progressive
 * twin has no honest whole-window goal to lend.
 */

const idOf = (v) => (v == null ? "" : String(v));

/**
 * @param {object} companyKpis    { [workspaceId]: KPI def[] }
 * @param {string} revenueKpiId   the canonical Total Revenue KPI's id
 * @param {string} hoursKpiId     the Technicians workspace's Hours Worked KPI id
 * @returns {object|null} the KPI def, or null when there is no such anchor
 */
export const findRevenuePerHourTwin = (companyKpis, revenueKpiId, hoursKpiId) => {
  const rev = idOf(revenueKpiId);
  const hrs = idOf(hoursKpiId);
  if (!companyKpis || typeof companyKpis !== "object" || !rev || !hrs) return null;
  const seen = new Set();
  let hit = null;
  Object.values(companyKpis).forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((k) => {
      if (hit || !k || seen.has(idOf(k._id))) return;
      seen.add(idOf(k._id));
      if (k.isDraft || k.disabled || k.retired || k.metricType !== "anchor") return;
      const o = k.optional;
      if (!o || o.operand !== "divide") return;
      if (idOf(o.sourceId1) !== rev || idOf(o.sourceId2) !== hrs) return;
      if (!(rawTargetOf(k) > 0)) return;
      hit = k;
    });
  });
  return hit;
};

/* The store keeps every rate as a 0–1 fraction (Beat the Yeti rate is one);
   this card prints and takes a goal in percent, so a derived rate is lifted to
   percent here, once, and everything downstream stays in one unit. */
export const derivedAmountOf = (amount, kind) => {
  const v = Number(amount) || 0;
  return kind === "%" ? v * 100 : v;
};

/* A derived metric's per-day points, in the card's own units. A day on which the
   metric could not be computed (no hours logged, so revenue ÷ hours divides by
   nothing) is left OUT rather than plotted as $0 — zero would claim "$0 an hour
   that day" and drag the line to the floor at every weekend. */
export const derivedSeriesOf = (derived) => (
  (derived?.dailySeries || [])
    .filter((p) => p && p.date && Number(p.value) > 0)
    .map((p) => ({ date: p.date, value: derivedAmountOf(p.value, derived.kind) }))
);
